/**
 * Sentry del navegador (spec docs/superpowers/specs/2026-09-28-sentry-design.md §6 y §7).
 *
 * El filtro de privacidad es el de application/libraries/Fd_sentry.php, regla por regla y con los
 * mismos topes; los vectores de scripts/fixtures/sentry/vectores.json los prueban vitest y el arnés
 * de PHP: cambia los dos a la vez. Nada de lookbehind ni de la bandera s en las expresiones
 * regulares: Safari anterior a 16.4 no parsearía el bundle entero y la app dejaría de funcionar.
 *
 * La configuración llega por atributos data- del <script> de facturadigital.min.js
 * (Fd_sentry::atributos_js() en noble/layout/scripts.php), sin script en línea. Sin DSN no se
 * inicializa nada.
 *
 * @sentry/browser 11 ya no tiene sendDefaultPii: lo sustituye dataCollection, que por omisión junta
 * la IP (Relay la infiere), encabezados, cookies, query strings y cuerpos. Aquí va todo apagado, y
 * además el filtro corre dos veces: en beforeSend y en el transporte, la última puerta antes de la
 * red (un error interno del SDK sale sin pasar por beforeSend, con la URL completa de la página).
 */
import { captureException, init, makeFetchTransport, setTag, setUser } from '@sentry/browser';

/**
 * La tabla de la sección 7, en el orden de Fd_sentry::REGLAS: UUID y CURP antes que RFC, secretos
 * antes que números. [0-9] y no \d (con /u, PHP casaría dígitos Unicode). RFC, CURP y correo llevan
 * u: la Ñ y las letras con acento son un carácter. El carácter previo se captura y vuelve con $1.
 * Tercer elemento: pasadas. Las reglas ancladas al inicio de la corrida que se comen lo que sigue
 * (base64 con relleno, 32+) corren dos veces, para un segundo valor pegado al primero.
 */
const REGLAS = [
  [/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[UUID]', 1],
  [/(^|[^A-Z0-9])[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[A-Z0-9][0-9](?![A-Z0-9])/giu, '$1[CURP]', 1],
  [/(^|[^A-Z0-9Ñ&])[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}(?![A-Z0-9])/giu, '$1[RFC]', 1],
  // Sin anclar y con cotas (RFC 5321 y DNS): tres correos pegados salen tapados los tres. \p{M}: un acento
  // descompuesto (NFD, al copiar de macOS); \p{Cf}: un invisible (espacio de ancho cero, guion suave);
  // \p{Cn}: una letra que este motor aún no conoce, que así cuenta como parte del correo. El lookahead no
  // cambia lo que casa (la parte local no tiene @): descarta rápido las posiciones sin una @ cerca.
  [/(?=[^@]{1,64}@)[\p{L}\p{M}\p{N}\p{Cf}\p{Cn}._%+-]{1,64}@[\p{L}\p{M}\p{N}\p{Cf}\p{Cn}.-]{1,253}\.[\p{L}\p{M}\p{Cn}]{2,63}/gu, '[CORREO]', 1],
  [/\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]+/g, '[SECRETO]', 1],
  [/\bwhsec_[A-Za-z0-9]+/g, '[SECRETO]', 1],
  [/\benc2:[A-Za-z0-9+\/=:._-]+/g, '[SECRETO]', 1],
  [/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[SECRETO]', 1],
  [/(^|[^A-Za-z0-9+\/_-])(?=[A-Za-z0-9+\/_-]*[0-9])(?=[A-Za-z0-9+\/_-]*[a-z])(?=[A-Za-z0-9+\/_-]*[A-Z])[A-Za-z0-9+\/_-]{20,}={1,2}/g, '$1[SECRETO]', 2],
  [/(^|[^A-Za-z0-9+_-])(?=[A-Za-z0-9+_-]*[0-9])(?=[A-Za-z0-9+_-]*[A-Za-z])[A-Za-z0-9+_-]{32,}={0,2}/g, '$1[SECRETO]', 2],
  [/[0-9]{10,}/g, '[NUM]', 1],
];

/**
 * Solo en las rutas de las URL: 20+ [A-Za-z0-9_-] al inicio de un segmento, con dígito, minúscula y
 * mayúscula, es un token (la activación del kiosko), aunque le siga un '.pdf'. Termina con un
 * lookahead negativo y no con $, igual que en PHP.
 */
const TOKEN_RUTA = /(^|\/)(?=[A-Za-z0-9_-]*[0-9])(?=[A-Za-z0-9_-]*[a-z])(?=[A-Za-z0-9_-]*[A-Z])[A-Za-z0-9_-]{20,}(?![A-Za-z0-9_-])/g;

/**
 * Hasta el último carácter que no puede ser parte de lo que las reglas tapan, con las mismas clases que la
 * regla de correo (acento NFD, invisible, letra desconocida). [\s\S] y no la bandera s.
 */
const FIN_SEGURO = /^[\s\S]*[^\p{L}\p{M}\p{N}\p{Cf}\p{Cn}&._%+@:\/=-]/u;

/** Topes de Fd_sentry::tapar(), en bytes de UTF-8 como allá: revisa 16 KB y deja salir 8 KB. */
const TOPE_ANTES = 16384;
const TOPE_DESPUES = 8192;
const CORTE = '…';
const BYTES_CORTE = 3;

const ILEGIBLE = '[ILEGIBLE]';
const OBJETO = '[OBJETO]';

/** De la petición solo salen estas claves, y de sus encabezados solo estos (sin distinguir mayúsculas). */
const ENCABEZADOS_OK = ['host', 'user-agent', 'accept', 'content-type', 'content-length', 'x-requested-with'];

/**
 * De extra solo sale lo de esta lista blanca: hoy, nada. Aquí nadie pone extra; lo llena el SDK solo, con
 * datos de la página: __serialized__ trae entero lo que se rechazó o se lanzó sin ser Error (el jqXHR de
 * un $.ajax con su responseText, que en staging es la sentencia SQL con nombre y domicilio; un Event con
 * el alt de su img) y arguments, los argumentos del callback que tronó. Lista blanca y no negra: lo que
 * agregue una versión nueva del SDK tampoco sale.
 */
const EXTRA_OK = [];

/**
 * A lo más cuántos eventos manda una carga de página: un error en un ciclo (o varios que se alternan, que
 * Dedupe no junta) agotaría la cuota del mes (5,000, compartida con PHP y con staging). Los que sobran se
 * cuentan como descartados en el reporte del cliente.
 */
const TOPE_POR_PAGINA = 10;

/** IDs que genera el SDK en el contexto trace: se conservan si son hexadecimales (no son datos de nadie). */
const IDS_TRAZA = ['trace_id', 'span_id', 'parent_span_id'];
const ID_TRAZA = /^[0-9a-f]{16}(?:[0-9a-f]{16})?$/;

/** Lo que no sale de un marco de la traza: variables y líneas de código. */
const MARCO_FUERA = ['vars', 'pre_context', 'context_line', 'post_context'];

/** Campos de una miga que son URL: van por limpiarUrl(). */
const CAMPOS_URL = ['url', 'from', 'to'];

/**
 * Las migas de clic y de teclado (ui.*) llevan el selector del elemento, y el SDK siempre le pega el
 * texto de aria-label, title y alt: el nombre de un trabajador (noble/nomina/periodo.php), la razón
 * social (cuenta/_shell_ini.php). El selector se rehace desde el elemento sin esos tres; sin elemento,
 * se corta en el primero que aparezca.
 */
const ATRIBUTOS_DE_TEXTO = /\[(?:aria-label|title|alt)="/;
const ATRIBUTOS_TECNICOS = ['type', 'name'];

/**
 * Integraciones por omisión que no se instalan. Console: en la versión 11 las migas de consola ya no
 * son una opción de breadcrumbsIntegration sino esta integración aparte (además envuelve console.*).
 * BrowserSession: mandaría una sesión a Sentry en cada carga de página, con el id del usuario; aquí
 * solo salen errores. CultureContext: idioma y zona horaria del navegador en cada evento, una señal de
 * ubicación que no hace falta. La prueba fija la lista exacta de las que quedan: una integración nueva
 * que traiga una actualización del SDK no entra sin revisarla.
 */
const INTEGRACIONES_FUERA = ['Console', 'BrowserSession', 'CultureContext'];

/**
 * Los errores de programación, con el mismo criterio con que jQuery 4 los avisa en la consola
 * (jQuery.Deferred.exceptionHook): los demás (un Error de negocio, un objeto) no son un bug.
 */
const ERRORES_DE_CODIGO = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

/**
 * Cuántas unidades de s caben en n bytes de UTF-8 sin partir un carácter (mb_strcut de PHP), o -1 si
 * cabe entero. Un par sustituto vale 4 bytes; un sustituto suelto, 3 (lo que ocuparía U+FFFD).
 */
function corteUtf8(s, n) {
  if (s.length * 3 <= n) return -1;
  let bytes = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    let b = c < 0x80 ? 1 : c < 0x800 ? 2 : 3;
    let par = false;
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        b = 4;
        par = true;
      }
    }
    if (bytes + b > n) return i;
    bytes += b;
    if (par) i++;
  }
  return -1;
}

/** Tapa en un texto lo que la tabla de la sección 7 considera dato personal o secreto. */
export function tapar(s) {
  let t = String(s);
  if (t === '') return '';
  let cortado = false;
  const i = corteUtf8(t, TOPE_ANTES);
  if (i !== -1) {
    // El corte pudo partir un RFC, un correo o un secreto, que la regla ya no reconocería: el último
    // pedazo se va hasta el separador anterior.
    cortado = true;
    const m = FIN_SEGURO.exec(t.slice(0, i));
    t = m ? m[0] : '';
  }
  for (const [re, reemplazo, pasadas] of REGLAS) {
    for (let p = 0; p < pasadas; p++) t = t.replace(re, reemplazo);
  }
  if (corteUtf8(t, TOPE_DESPUES) !== -1) {
    cortado = true;
    t = t.slice(0, corteUtf8(t, TOPE_DESPUES - BYTES_CORTE));
  }
  return cortado ? t + CORTE : t;
}

function sinQuery(s) {
  const i = s.search(/[?#]/);
  return i === -1 ? s : s.slice(0, i);
}

/**
 * La URL sin query string ni fragmento, decodificada (%XX) y tapada; los segmentos de la ruta que
 * parecen token quedan como [TOKEN]. Un %3F o un %23 también empiezan la query o el fragmento: PHP
 * decodifica todo y vuelve a cortar; aquí se corta antes, en ese escape, que es lo mismo (cada %XX da un
 * byte y los ? y # literales ya se fueron), y lo que venía después no cuenta, tampoco si no era UTF-8.
 * Como rawurldecode de PHP, solo se decodifica %XX (%ZZ y + se quedan); un %XX que no forma UTF-8
 * (decodeURIComponent lanza) sale [ILEGIBLE], como en PHP, donde las reglas con /u no pueden leerlo.
 */
export function limpiarUrl(u) {
  let s = sinQuery(String(u));
  const q = s.search(/%(?:3[Ff]|23)/);
  if (q !== -1) s = s.slice(0, q);
  try {
    s = s.replace(/(?:%[0-9A-Fa-f]{2})+/g, (m) => decodeURIComponent(m));
  } catch (e) {
    return ILEGIBLE;
  }
  return tapar(s.replace(TOKEN_RUTA, '$1[TOKEN]'));
}

/**
 * Como (string) de PHP antes de buscar 10+ dígitos: los enteros tal cual y los fraccionarios con
 * precision = 14 (0.1 + 0.2 da "0.3" y no "0.30000000000000004", que parecería un número largo).
 */
function numeroLargo(n) {
  const s = typeof n === 'number' && isFinite(n) && !Number.isInteger(n) ? String(Number(n.toPrecision(14))) : String(n);
  return /[0-9]{10,}/.test(s);
}

/**
 * Recursivo, como Fd_sentry::tapar_todo(): textos (valores y claves) tapados, números de 10+ dígitos
 * como [NUM], objetos que no son planos (lo que en PHP sería un objeto) como [OBJETO], lo demás igual.
 */
function taparTodo(v) {
  if (typeof v === 'string') return tapar(v);
  if (typeof v === 'number' || typeof v === 'bigint') return numeroLargo(v) ? '[NUM]' : v;
  if (Array.isArray(v)) return v.map(taparTodo);
  if (v !== null && typeof v === 'object') {
    const proto = Object.getPrototypeOf(v);
    if (proto !== Object.prototype && proto !== null) return OBJETO;
    const fuera = {};
    for (const k of Object.keys(v)) fuera[tapar(k)] = taparTodo(v[k]);
    return fuera;
  }
  return v;
}

/** Solo los encabezados de la lista blanca, con sus valores tapados. */
function encabezados(h) {
  const fuera = {};
  if (h === null || typeof h !== 'object') return fuera;
  for (const k of Object.keys(h)) {
    if (ENCABEZADOS_OK.indexOf(k.toLowerCase()) !== -1) fuera[k] = taparTodo(h[k]);
  }
  return fuera;
}

/** De la petición solo salen url (limpia), method y los encabezados de la lista blanca. */
function limpiarPeticion(req) {
  const fuera = {};
  if (req.url != null) fuera.url = limpiarUrl(req.url);
  if (req.method != null) fuera.method = taparTodo(req.method);
  if (req.headers != null) fuera.headers = encabezados(req.headers);
  return fuera;
}

/**
 * Marcos nuevos: URL y función tapadas, sin variables ni líneas de código. La URL de un marco puede
 * ser la de la página (un onclick en línea) con su query string.
 */
function limpiarTraza(traza) {
  if (!traza || !Array.isArray(traza.frames)) return traza;
  const frames = traza.frames.map((f) => {
    const m = {};
    for (const k of Object.keys(f)) {
      if (MARCO_FUERA.indexOf(k) === -1) m[k] = f[k];
    }
    if (typeof m.filename === 'string') m.filename = limpiarUrl(m.filename);
    if (typeof m.abs_path === 'string') m.abs_path = limpiarUrl(m.abs_path);
    if (typeof m.function === 'string') m.function = tapar(m.function);
    if (typeof m.module === 'string') m.module = tapar(m.module);
    return m;
  });
  return { ...traza, frames };
}

function limpiarExcepcion(x) {
  const e = { ...x };
  if (typeof e.type === 'string') e.type = tapar(e.type);
  if (typeof e.value === 'string') e.value = tapar(e.value);
  if (e.stacktrace) e.stacktrace = limpiarTraza(e.stacktrace);
  if (e.mechanism && e.mechanism.data) e.mechanism = { ...e.mechanism, data: taparTodo(e.mechanism.data) };
  return e;
}

/** Cada contexto tapado; en trace, los IDs hexadecimales se conservan (IDS_TRAZA). */
function limpiarContextos(ctx) {
  const fuera = {};
  for (const nombre of Object.keys(ctx)) {
    const datos = ctx[nombre];
    const limpio = taparTodo(datos);
    if (nombre === 'trace' && datos && typeof datos === 'object' && limpio && typeof limpio === 'object') {
      for (const k of IDS_TRAZA) {
        if (typeof datos[k] === 'string' && ID_TRAZA.test(datos[k])) limpio[k] = datos[k];
      }
    }
    fuera[nombre] = limpio;
  }
  return fuera;
}

/**
 * El beforeSend (y la puerta del transporte): la sección 7 sobre el evento, o null si algo truena
 * (el evento no sale). Además de la tabla, como en PHP: la petición es lista blanca, del usuario solo
 * queda el id, los marcos pierden variables y líneas de código, fuera los adjuntos y los IDs de la
 * traza se conservan. Propio del navegador: extra es lista blanca (EXTRA_OK, hoy vacía: fuera
 * __serialized__, arguments y lo que venga), y debug_meta lleva cada URL de bundle limpia igual que los
 * marcos, para que Sentry siga casando los Debug ID de los mapas de fuente.
 */
export function filtrarEvento(ev, hint) {
  try {
    if (ev.message !== undefined) ev.message = taparTodo(ev.message);
    if (ev.logentry) {
      const l = { ...ev.logentry };
      delete l.params;
      if (typeof l.message === 'string') l.message = tapar(l.message);
      if (typeof l.formatted === 'string') l.formatted = tapar(l.formatted);
      ev.logentry = l;
    }
    if (ev.request) ev.request = limpiarPeticion(ev.request);
    if (ev.exception && Array.isArray(ev.exception.values)) {
      ev.exception = { ...ev.exception, values: ev.exception.values.map(limpiarExcepcion) };
    }
    if (ev.stacktrace) ev.stacktrace = limpiarTraza(ev.stacktrace);
    if (ev.user) {
      const id = ev.user.id;
      if (id !== undefined && id !== null && String(id) !== '') ev.user = { id: tapar(String(id)) };
      else delete ev.user;
    }
    if (ev.tags) ev.tags = taparTodo(ev.tags);
    if (ev.extra) {
      const x = {};
      for (const k of Object.keys(ev.extra)) {
        if (EXTRA_OK.indexOf(k) !== -1) x[k] = ev.extra[k];
      }
      if (Object.keys(x).length > 0) ev.extra = taparTodo(x);
      else delete ev.extra;
    }
    if (ev.contexts) ev.contexts = limpiarContextos(ev.contexts);
    if (typeof ev.transaction === 'string') ev.transaction = limpiarUrl(ev.transaction);
    if (Array.isArray(ev.breadcrumbs)) ev.breadcrumbs = ev.breadcrumbs.map((b) => filtrarMigaja(b)).filter(Boolean);
    if (ev.debug_meta && Array.isArray(ev.debug_meta.images)) {
      ev.debug_meta = {
        ...ev.debug_meta,
        images: ev.debug_meta.images.map((im) => (im && typeof im.code_file === 'string' ? { ...im, code_file: limpiarUrl(im.code_file) } : im)),
      };
    }
    if (hint && Array.isArray(hint.attachments)) hint.attachments = [];
    return ev;
  } catch (e) {
    return null;
  }
}

/** El elemento de una miga de DOM: la pista trae el evento (su target) o el elemento mismo. */
function elementoDe(pista) {
  const e = pista && pista.event;
  const el = e && e.target ? e.target : e;
  return el && typeof el.tagName === 'string' ? el : null;
}

/**
 * El selector de un elemento como lo arma el SDK (hasta 5 niveles, 80 caracteres, sin <html>): tag,
 * #id, .clases y los atributos técnicos, sin el texto de aria-label, title ni alt.
 */
function selectorSeguro(el) {
  const partes = [];
  let largo = 0;
  for (let n = el, alto = 0; n && alto < 5; n = n.parentNode, alto++) {
    const tag = typeof n.tagName === 'string' ? n.tagName.toLowerCase() : '';
    if (!tag || tag === 'html') break;
    let s = tag;
    if (typeof n.id === 'string' && n.id) s += '#' + n.id;
    if (typeof n.className === 'string' && n.className.trim()) s += '.' + n.className.trim().split(/\s+/).join('.');
    for (const k of ATRIBUTOS_TECNICOS) {
      const valor = typeof n.getAttribute === 'function' ? n.getAttribute(k) : null;
      if (valor) s += '[' + k + '="' + valor + '"]';
    }
    if (alto > 0 && largo + partes.length * 3 + s.length >= 80) break;
    partes.push(s);
    largo += s.length;
  }
  return partes.reverse().join(' > ');
}

/**
 * El beforeBreadcrumb (con su pista) y el paso por cada miga del evento (sin pista): fuera las de
 * consola (defensa extra: la integración Console ni se instala); las de ui.* sin el texto de
 * aria-label, title ni alt; mensaje y datos tapados, las URL (url, from, to) limpias. null si algo
 * truena (la miga no sale).
 */
export function filtrarMigaja(b, pista) {
  try {
    if (!b || b.category === 'console') return null;
    const m = { ...b };
    if (typeof m.category === 'string' && m.category.indexOf('ui.') === 0) {
      const el = elementoDe(pista);
      if (el) {
        m.message = selectorSeguro(el);
      } else if (typeof m.message === 'string') {
        const i = m.message.search(ATRIBUTOS_DE_TEXTO);
        if (i !== -1) m.message = m.message.slice(0, i);
      }
    }
    if (typeof m.message === 'string') m.message = tapar(m.message);
    if (m.data && typeof m.data === 'object') {
      const datos = {};
      for (const k of Object.keys(m.data)) {
        const v = m.data[k];
        datos[tapar(k)] = CAMPOS_URL.indexOf(k) !== -1 && typeof v === 'string' ? limpiarUrl(v) : taparTodo(v);
      }
      m.data = datos;
    }
    return m;
  } catch (e) {
    return null;
  }
}

/**
 * La última puerta antes de la red: cada evento del sobre pasa otra vez por filtrarEvento (un error
 * interno del SDK, con data.__sentry__, se salta beforeSend y los procesadores, pero no esto) y solo
 * salen eventos y reportes de descartes (client_report, conteos sin datos). Lo demás (sesiones,
 * adjuntos, spans, logs, replay, feedback) no sale: aquí solo se reportan errores.
 */
export function filtrarSobre(sobre) {
  const quedan = [];
  for (const item of sobre[1]) {
    const tipo = item && item[0] && item[0].type;
    if (tipo === 'event') {
      const ev = filtrarEvento(item[1]);
      if (ev) quedan.push([item[0], ev]);
    } else if (tipo === 'client_report') {
      quedan.push(item);
    }
  }
  return [sobre[0], quedan];
}

/**
 * El transporte del SDK (hacer: makeFetchTransport) con filtrarSobre delante y el tope de eventos por
 * carga de página (un transporte por init, uno por página): los que pasan del tope no salen y se cuentan
 * como descartados (before_send) en el reporte del cliente. Si algo truena, nada sale.
 */
export function transporteFiltrado(hacer, tope = TOPE_POR_PAGINA) {
  return (opcionesTransporte) => {
    const t = hacer(opcionesTransporte);
    let eventos = 0;
    return {
      ...t,
      send(sobre) {
        let limpio;
        try {
          const [cabecera, items] = filtrarSobre(sobre);
          const quedan = [];
          for (const item of items) {
            if (item[0].type === 'event') {
              if (eventos >= tope) {
                contarDescartado(opcionesTransporte);
                continue;
              }
              eventos++;
            }
            quedan.push(item);
          }
          limpio = [cabecera, quedan];
        } catch (e) {
          return Promise.resolve({});
        }
        return t.send(limpio);
      },
    };
  };
}

function contarDescartado(opcionesTransporte) {
  try {
    if (opcionesTransporte && typeof opcionesTransporte.recordDroppedEvent === 'function') {
      opcionesTransporte.recordDroppedEvent('before_send', 'error');
    }
  } catch (e) {
    // el conteo es informativo
  }
}

/**
 * Los errores de programación (ERRORES_DE_CODIGO) dentro de un .then de jQuery Deferred (los catálogos
 * de CFDI y de Carta Porte, los jqXHR): jQuery los atrapa, avisa en la consola y Sentry no los vería.
 * El gancho se encadena al que había (el aviso de la consola se queda) y nunca rompe jQuery. Pasan por
 * el mismo filtro, allowUrls, la puerta y el tope; si después el mismo error acaba en un
 * unhandledrejection, el SDK no lo manda otra vez (lo marca como ya capturado). jQuery lee
 * jQuery.Deferred.exceptionHook al momento del error, así que sirve también sobre el envoltorio de
 * jQuery Migrate. true si quedó puesto.
 */
export function ganchoDeferred(jq, capturar) {
  const D = jq && jq.Deferred;
  if (!D) return false;
  const previo = D.exceptionHook;
  D.exceptionHook = function (error) {
    try {
      if (error instanceof Error && ERRORES_DE_CODIGO.test(error.name)) {
        capturar(error, { mechanism: { type: 'auto.jquery.deferred', handled: false } });
      }
    } catch (e) {
      // nunca rompe jQuery
    }
    return typeof previo === 'function' ? previo.apply(this, arguments) : undefined;
  };
  return true;
}

/** Los atributos data-sentry-* del <script> (Fd_sentry::atributos_js), o null sin DSN o sin entorno. */
export function leerConfig(script) {
  const d = (script && script.dataset) || {};
  if (!d.sentryDsn || !d.sentryEntorno) return null;
  return {
    dsn: d.sentryDsn,
    entorno: d.sentryEntorno,
    release: d.sentryRelease || '',
    emisor: d.sentryEmisor || '',
    usuario: d.sentryUsuario || '',
  };
}

/**
 * dataCollection con todo apagado (sustituye a sendDefaultPii en la versión 11). userInfo en false
 * manda infer_ip: 'never' en cada evento: Relay no guarda la IP. De los encabezados de la página el
 * SDK junta Referer y User-Agent; lo que no está en la lista blanca le llega al filtro ya como
 * '[Filtered]' y el filtro lo quita.
 */
function datosApagados() {
  return {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: { allow: ENCABEZADOS_OK.slice() }, response: false },
    httpBodies: [],
    urlQueryParams: false,
    graphQL: { document: false, variables: false },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    stackFrameVariables: false,
    frameContextLines: 0,
  };
}

/**
 * Las opciones exactas de init() (spec §6). Pura: vitest las revisa. Sin tracing ni replay (ni sus
 * integraciones ni tracesSampleRate). allowUrls mira el marco de arriba que tiene URL: descarta lo que
 * tronó en un vendor, en un script en línea o en una extensión. No descarta los eventos sin marcos (un
 * rechazo o un throw de algo que no es Error, venga de donde venga) ni el código sin URL (la consola,
 * eval) que truena dentro de un callback que el SDK envuelve (setTimeout, listeners), porque su marco de
 * arriba con URL es el envoltorio, que vive en este bundle. Esos salen filtrados como todos y cuentan
 * para el tope por página.
 */
export function opciones(c, hacerTransporte = makeFetchTransport) {
  return {
    dsn: c.dsn,
    environment: c.entorno,
    release: c.release || undefined,
    dataCollection: datosApagados(),
    allowUrls: [/\/theme2026\/dist\/js\//],
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-(web-)?extension:\/\//],
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'ResizeObserver loop completed with undelivered notifications'],
    integrations: (porOmision) => porOmision.filter((i) => INTEGRACIONES_FUERA.indexOf(i.name) === -1),
    beforeSend: filtrarEvento,
    beforeBreadcrumb: filtrarMigaja,
    transport: transporteFiltrado(hacerTransporte),
  };
}

/**
 * Inicializa el SDK con los atributos del <script> y pone el gancho de jQuery Deferred (jq: el jQuery de
 * la página, que los vendors cargan antes del bundle); nunca lanza. true si quedó activo: con un DSN mal
 * escrito init() no lanza, deja un cliente sin DSN que no manda nada, y eso cuenta como apagado.
 */
export function iniciarSentry(script, hacerTransporte, jq) {
  const c = leerConfig(script);
  if (!c) return false;
  try {
    const cliente = init(opciones(c, hacerTransporte));
    if (!cliente || !cliente.getDsn()) return false;
    if (c.emisor) setTag('emisor_id', c.emisor);
    if (c.usuario) setUser({ id: c.usuario });
    ganchoDeferred(jq !== undefined ? jq : typeof window !== 'undefined' ? window.jQuery : undefined, captureException);
    return true;
  } catch (e) {
    return false;
  }
}

// Al evaluarse el bundle (script clásico, antes que el resto de app.js): document.currentScript es su
// <script>. FD.sentryPrueba() lanza desde nuestro bundle y en otro turno: un error que sale directo
// de algo tecleado en la consola del navegador no llega a window.onerror (ni a Sentry).
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const activo = iniciarSentry(document.currentScript || document.querySelector('script[data-sentry-dsn]'));
  window.FD = window.FD || {};
  window.FD.sentryPrueba = function () {
    if (!activo) return 'Sentry no está activo en esta página: sin data-sentry-dsn, o con un DSN que no sirve (ver la consola).';
    setTimeout(function () {
      throw new Error('Prueba de Sentry');
    }, 0);
    return 'Error de prueba lanzado: búscalo en el proyecto facturadigital-js de Sentry.';
  };
}
